import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, UserPlus, Mail, Phone, Pencil, Check, X, Copy, ExternalLink, Cake, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Avatar from './Avatar';
import { invalidateFrequentContactsCache } from './ShareWhatsappButton';

const API = process.env.REACT_APP_BACKEND_URL;

const WhatsAppMini = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.48A11.94 11.94 0 0 0 12.05 0C5.49 0 .2 5.29.2 11.85c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.85 11.85 0 0 0 5.65 1.44h.01c6.55 0 11.85-5.3 11.85-11.85 0-3.17-1.24-6.15-3.39-8.43zM12.06 21.8h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.22-3.8 1 1.02-3.7-.24-.38a9.86 9.86 0 0 1-1.52-5.27c0-5.45 4.43-9.88 9.89-9.88 2.64 0 5.12 1.03 6.99 2.9a9.83 9.83 0 0 1 2.9 6.99c0 5.45-4.43 9.94-9.83 9.94zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.93 1.17-.17.2-.34.22-.63.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.53.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.19-.24-.58-.48-.5-.66-.51h-.56c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.5 1.69.64.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z"/>
  </svg>
);

export default function FamilyView() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteBirthday, setInviteBirthday] = useState('');
  const [inviting, setInviting] = useState(false);
  const [pendingInvite, setPendingInvite] = useState(null); // {name, email, whatsapp_link, mailto_link, share_url, email_sent}
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', phone: '', birthday: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const loadMembers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/family/members`, { withCredentials: true });
      setMembers(data);
    } catch (error) {
      toast.error('Error al cargar los miembros');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/family/invite`,
        {
          email: inviteEmail,
          name: inviteName,
          phone: invitePhone.trim() || undefined,
          birthday: inviteBirthday || undefined,
          role: 'member',
        },
        { withCredentials: true }
      );

      if (data.email_sent) {
        toast.success(`Email enviado a ${data.email}`);
      } else {
        toast.info('Invitación creada. Elegí cómo enviarla:');
      }

      // Always show the share panel so the user has a reliable way to notify
      setPendingInvite({
        name: data.name,
        email: data.email,
        whatsapp_link: data.whatsapp_link,
        mailto_link: data.mailto_link,
        share_url: data.share_url,
        email_sent: !!data.email_sent,
        email_disabled: !!data.email_disabled,
      });

      setInviteEmail('');
      setInviteName('');
      setInvitePhone('');
      setInviteBirthday('');
      setShowInviteForm(false);
      invalidateFrequentContactsCache();
      loadMembers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al enviar la invitación');
    } finally {
      setInviting(false);
    }
  };

  const copyInviteLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado al portapapeles');
    } catch {
      toast.error('No pudimos copiar el link');
    }
  };

  const resendMember = async (member) => {
    try {
      const { data } = await axios.get(
        `${API}/api/family/members/${member.id}/invite-links`,
        { withCredentials: true }
      );
      setPendingInvite({
        name: member.name,
        email: member.email,
        whatsapp_link: data.whatsapp_link,
        mailto_link: data.mailto_link,
        share_url: data.share_url,
        email_sent: false,
        email_disabled: true,
      });
    } catch {
      toast.error('No pudimos generar el link de reenvío');
    }
  };

  const startEdit = (member) => {
    setEditingId(member.id);
    setEditDraft({ name: member.name || '', phone: member.phone || '', birthday: member.birthday || '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ name: '', phone: '', birthday: '' });
  };

  const saveEdit = async (memberId) => {
    if (editDraft.name.trim().length < 2) {
      toast.error('El nombre debe tener al menos 2 caracteres');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.patch(
        `${API}/api/family/members/${memberId}`,
        { name: editDraft.name, phone: editDraft.phone, birthday: editDraft.birthday },
        { withCredentials: true }
      );
      toast.success('Miembro actualizado');
      invalidateFrequentContactsCache();
      setEditingId(null);
      loadMembers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No pudimos actualizar el miembro');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteMember = async (memberId, memberName) => {
    if (!window.confirm(`¿Querés eliminar a ${memberName}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/api/family/members/${memberId}`, { withCredentials: true });
      toast.success('Familiar eliminado');
      invalidateFrequentContactsCache();
      loadMembers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No pudimos eliminar al familiar');
    }
  };

  // Compute days until next birthday given a YYYY-MM-DD string.
  const daysUntilBirthday = (birthday) => {
    if (!birthday) return null;
    try {
      const [_, m, d] = birthday.split('-').map(Number);
      if (!m || !d) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let next = new Date(today.getFullYear(), m - 1, d);
      if (next < today) next = new Date(today.getFullYear() + 1, m - 1, d);
      return Math.round((next - today) / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-12 h-12 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="family-view">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-2" >
            Familia
          </h1>
          <p className="text-muted-foreground">Miembros del grupo familiar</p>
        </div>
        <button
          data-testid="invite-member-btn"
          onClick={() => setShowInviteForm(!showInviteForm)}
          className="px-6 py-3 bg-[#D97706] text-white rounded-full font-semibold hover:bg-[#B45309] transition-all hover:-translate-y-0.5 shadow-lg flex items-center gap-2"
        >
          <UserPlus className="w-5 h-5" strokeWidth={2} />
          Invitar
        </button>
      </div>

      {showInviteForm && (
        <div className="bg-surface rounded-2xl shadow-md p-8">
          <h2 className="text-xl font-bold text-foreground mb-4">Invitar Miembro</h2>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Nombre</label>
              <input
                data-testid="invite-name-input"
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                placeholder="Nombre del miembro"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">Email</label>
              <input
                data-testid="invite-email-input"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                placeholder="email@ejemplo.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">
                Teléfono <span className="text-muted-foreground font-normal">(para enviar por WhatsApp)</span>
              </label>
              <input
                data-testid="invite-phone-input"
                type="tel"
                value={invitePhone}
                onChange={(e) => setInvitePhone(e.target.value)}
                className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                placeholder="Ej: +54 9 11 5566 7788"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Al crear la invitación te mostramos 3 opciones para enviarla: WhatsApp, Email o copiar el link.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-muted-foreground mb-2">
                Fecha de nacimiento <span className="text-muted-foreground font-normal">(opcional, para avisarte 7 días antes)</span>
              </label>
              <input
                data-testid="invite-birthday-input"
                type="date"
                value={inviteBirthday}
                onChange={(e) => setInviteBirthday(e.target.value)}
                className="w-full px-4 py-3 bg-surface-2 border border-transparent rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#D97706]"
              />
            </div>

            <div className="flex gap-3">
              <button
                data-testid="send-invite-btn"
                type="submit"
                disabled={inviting}
                className="flex-1 py-3 bg-[#D97706] text-white rounded-full font-semibold hover:bg-[#B45309] transition-all disabled:opacity-50"
              >
                {inviting ? 'Enviando...' : 'Enviar Invitación'}
              </button>
              <button
                type="button"
                onClick={() => setShowInviteForm(false)}
                className="px-6 py-3 bg-slate-200 text-muted-foreground rounded-full font-semibold hover:bg-slate-300 transition-all"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {pendingInvite && (
        <div
          data-testid="pending-invite-panel"
          className="bg-surface rounded-2xl shadow-lg p-6 sm:p-8 border-2 border-[#D97706]/40"
        >
          <div className="flex items-start justify-between mb-4 gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-primary">
                Enviar invitación a
              </p>
              <h2 className="text-xl font-bold text-foreground">
                {pendingInvite.name}{' '}
                <span className="text-muted-foreground font-normal text-sm">({pendingInvite.email})</span>
              </h2>
              {pendingInvite.email_sent ? (
                <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">✓ Email enviado. También podés compartirla por otros medios.</p>
              ) : (
                <p className="text-sm text-muted-foreground mt-1">
                  Elegí cómo enviar la invitación. Los emails automáticos pueden fallar si no configuraste un dominio en Resend.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPendingInvite(null)}
              className="p-2 rounded-full hover:bg-muted text-muted-foreground"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a
              href={pendingInvite.whatsapp_link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="invite-send-whatsapp"
              className="flex items-center justify-center gap-2 py-3 bg-emerald-500 text-white rounded-full font-semibold hover:bg-[#1EBE5D] transition-all shadow-md hover:-translate-y-0.5"
            >
              <WhatsAppMini /> WhatsApp
            </a>
            <a
              href={pendingInvite.mailto_link}
              data-testid="invite-send-email"
              className="flex items-center justify-center gap-2 py-3 bg-[#1E3A5F] text-white rounded-full font-semibold hover:bg-[#152B47] transition-all shadow-md hover:-translate-y-0.5"
            >
              <Mail className="w-4 h-4" strokeWidth={2} /> Email
            </a>
            <button
              type="button"
              data-testid="invite-copy-link"
              onClick={() => copyInviteLink(pendingInvite.share_url)}
              className="flex items-center justify-center gap-2 py-3 bg-muted text-foreground rounded-full font-semibold hover:bg-slate-200 transition-all"
            >
              <Copy className="w-4 h-4" strokeWidth={2} /> Copiar link
            </button>
          </div>

          <p className="text-xs text-muted-foreground mt-3 flex items-center gap-2">
            <ExternalLink className="w-3 h-3" />
            <span className="truncate">{pendingInvite.share_url}</span>
          </p>
        </div>
      )}

      <div className="bg-surface rounded-2xl shadow-md p-8">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Tu grupo familiar</h2>

        <div
          data-testid="family-owner-card"
          className="flex items-center gap-4 p-4 bg-gradient-to-r from-[#1E3A5F]/5 to-[#D97706]/5 rounded-2xl mb-3 border border-foreground/10"
        >
          <Avatar name={user?.name} picture={user?.picture} size="md" testId="owner-avatar" />
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">{user?.name}</h3>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Mail className="w-3 h-3" strokeWidth={2} />
              {user?.email}
            </p>
          </div>
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-[#D97706] text-white">Tú</span>
        </div>

        {members.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-muted-foreground">Aún no invitaste a nadie</p>
            <p className="text-muted-foreground text-sm mt-1">Invita a tu familia para compartir tareas y documentos</p>
          </div>
        ) : (
          <div className="space-y-3">
            {members.map((member) => (
              <div
                key={member.id}
                data-testid={`family-member-${member.id}`}
                className="flex items-center gap-4 p-4 bg-surface-2 rounded-2xl hover:shadow-md transition-all"
              >
                <Avatar name={member.name} picture={member.picture} size="md" />
                <div className="flex-1 min-w-0">
                  {editingId === member.id ? (
                    <div className="space-y-2">
                      <input
                        data-testid={`edit-member-name-${member.id}`}
                        type="text"
                        value={editDraft.name}
                        onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                        className="w-full px-3 py-1.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                        placeholder="Nombre"
                      />
                      <input
                        data-testid={`edit-member-phone-${member.id}`}
                        type="tel"
                        value={editDraft.phone}
                        onChange={(e) => setEditDraft({ ...editDraft, phone: e.target.value })}
                        className="w-full px-3 py-1.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                        placeholder="Teléfono con código país (opcional)"
                      />
                      <input
                        data-testid={`edit-member-birthday-${member.id}`}
                        type="date"
                        value={editDraft.birthday}
                        onChange={(e) => setEditDraft({ ...editDraft, birthday: e.target.value })}
                        className="w-full px-3 py-1.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]"
                        placeholder="Fecha de nacimiento (opcional)"
                      />
                    </div>
                  ) : (
                    <>
                      <h3 className="font-semibold text-foreground truncate">{member.name}</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3 flex-shrink-0" strokeWidth={2} />
                        <span className="truncate">{member.email}</span>
                      </p>
                      {member.phone && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3" strokeWidth={2} />
                          {member.phone}
                        </p>
                      )}
                      {member.birthday && (() => {
                        const days = daysUntilBirthday(member.birthday);
                        const label = days === 0
                          ? 'cumple hoy 🎂'
                          : days === 1
                            ? 'cumple mañana 🎂'
                            : days != null && days <= 7
                              ? `en ${days} días`
                              : member.birthday.slice(5).replace('-', '/');
                        return (
                          <p
                            data-testid={`member-birthday-${member.id}`}
                            className={`text-xs flex items-center gap-1 mt-0.5 ${
                              days != null && days <= 7 ? 'text-[#7C3AED] font-semibold' : 'text-muted-foreground'
                            }`}
                          >
                            <Cake className="w-3 h-3" strokeWidth={2} />
                            {label}
                          </p>
                        );
                      })()}
                    </>
                  )}
                </div>

                {editingId === member.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`save-member-${member.id}`}
                      onClick={() => saveEdit(member.id)}
                      disabled={savingEdit}
                      className="p-2 bg-[#4CAF50] text-white rounded-full hover:bg-[#45a049] disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="p-2 bg-slate-200 text-muted-foreground rounded-full hover:bg-slate-300"
                    >
                      <X className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                  </div>
                ) : (
                  <>
                    {member.status === 'pending' && (
                      <button
                        data-testid={`resend-wa-${member.id}`}
                        onClick={() => resendMember(member)}
                        title="Reenviar invitación"
                        className="p-2 text-emerald-500 dark:text-emerald-400 hover:bg-emerald-500/10 rounded-full transition-all"
                      >
                        <WhatsAppMini />
                      </button>
                    )}
                    <button
                      data-testid={`edit-member-btn-${member.id}`}
                      onClick={() => startEdit(member)}
                      className="p-2 text-muted-foreground hover:text-foreground hover:bg-slate-200 rounded-full transition-all"
                      title="Editar miembro"
                    >
                      <Pencil className="w-4 h-4" strokeWidth={2} />
                    </button>
                    <button
                      data-testid={`delete-member-btn-${member.id}`}
                      onClick={() => deleteMember(member.id, member.name)}
                      className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                      title="Eliminar familiar"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={2} />
                    </button>
                    <span
                      data-testid={`member-status-${member.id}`}
                      className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        member.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {member.status === 'pending' ? 'Pendiente' : 'Aceptado'}
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
